<script setup>
import { loadStripe } from '@stripe/stripe-js';
import {
    computed,
    onMounted,
    ref, shallowRef,
} from 'vue';

const props = defineProps({
    group: {
        type: Object,
        required: true,
    },
    donation: {
        type: Object,
        required: true,
    },
    stripePublishableKey: {
        type: String,
        required: true,
    },
});

const amount = ref(null);
const cart = shallowRef(null);

const platformFee = computed(() => {
    return amount.value ? ((amount.value * 0.07) + 0.30) : 0;
});

const total = computed(() => {
    return amount.value + platformFee.value;
});

const card = ref(null);
const cardElement = shallowRef(null);
const stripe = shallowRef(null);

onMounted(async() => {
    // Setup stripe using loadStripe
    // loadStripe handles including the stripe.js script tag within the document and loading stripe
    // See https://github.com/stripe/stripe-js
    stripe.value = await loadStripe(props.stripePublishableKey);

    // Create an instance of stripe elements
    const elements = stripe.value.elements({
        fonts: [
            {
                cssSrc: 'https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap',
            },
        ],
    });

    cardElement.value = elements.create('card', {
        hidePostalCode: true,
        style: {
            base: {
                fontFamily: 'sans-serif',
                fontSize: '14px',
            },
        },
    });

    cardElement.value.mount(card.value);
});


const errors = ref([]);
const state = ref('idle');

const handleSubmit = async(event) => {
    state.value = 'loading';

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    const formData = new FormData(event.target);

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken,
    };

    const payload = {
        options: {
            donationAmount: amount.value,
            group: formData.get('options[group]'),
        },
        purchasableId: formData.get('purchasableId'),
        billingAddress: {
            fullName: formData.get('billingAddress[fullName]'),
            countryCode: formData.get('billingAddress[countryCode]'),
            addressLine1: formData.get('billingAddress[addressLine1]'),
            locality: formData.get('billingAddress[locality]'),
            administrativeArea: formData.get('billingAddress[administrativeArea]'),
            postalCode: formData.get('billingAddress[postalCode]'),
        },
        shippingAddressSameAsBilling: true,
        email: formData.get('email'),
    };

    const cartResponse = await fetch('/actions/commerce/cart/update-cart', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });

    const cartData = await cartResponse.json();

    if (cartData.errors) {
        state.value = 'error';

        Object.values(cartData.errors).forEach((error) => {
            errors.value = [...errors.value, ...error];
        });

        return;
    }

    cart.value = cartData.cart;

    // Process payment
    const { paymentMethod, error } = await stripe.value.createPaymentMethod({
        type: 'card',
        card: cardElement.value,
    });

    if (error) {
        state.value = 'error';
        errors.value = [error];
        return;
    }


    const paymentResponse = await fetch('/actions/commerce/payments/pay', {
        method: 'POST',
        headers,
        body: JSON.stringify({
            paymentForm: {
                stripe: {
                    paymentMethodId: paymentMethod.id,
                },
            },
        }),
    });

    const paymentData = await paymentResponse.json();

    if (paymentResponse.status !== 200 || !paymentData.transactionId) {
        state.value = 'error';
        const message = paymentData.message || 'An error occurred while processing your payment.';
        errors.value = [message];
    }

    event.target.reset();

    state.value = 'success';
};

const countries = [
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AL', name: 'Albania' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'AD', name: 'Andorra' },
    { code: 'AO', name: 'Angola' },
    { code: 'AI', name: 'Anguilla' },
    { code: 'AQ', name: 'Antarctica' },
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'AR', name: 'Argentina' },
    { code: 'AM', name: 'Armenia' },
    { code: 'AW', name: 'Aruba' },
    { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' },
    { code: 'AZ', name: 'Azerbaijan' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BB', name: 'Barbados' },
    { code: 'BY', name: 'Belarus' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BZ', name: 'Belize' },
    { code: 'BJ', name: 'Benin' },
    { code: 'BM', name: 'Bermuda' },
    { code: 'BT', name: 'Bhutan' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BW', name: 'Botswana' },
    { code: 'BV', name: 'Bouvet Island' },
    { code: 'BR', name: 'Brazil' },
    { code: 'IO', name: 'British Indian Ocean Territory' },
    { code: 'BN', name: 'Brunei Darussalam' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'BF', name: 'Burkina Faso' },
    { code: 'BI', name: 'Burundi' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'CM', name: 'Cameroon' },
    { code: 'CA', name: 'Canada' },
    { code: 'CV', name: 'Cape Verde' },
    { code: 'KY', name: 'Cayman Islands' },
    { code: 'CF', name: 'Central African Republic' },
    { code: 'TD', name: 'Chad' },
    { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' },
    { code: 'CX', name: 'Christmas Island' },
    { code: 'CC', name: 'Cocos (Keeling) Islands' },
    { code: 'CO', name: 'Colombia' },
    { code: 'KM', name: 'Comoros' },
    { code: 'CG', name: 'Congo' },
    { code: 'CD', name: 'Congo, Democratic Republic of the' },
    { code: 'CK', name: 'Cook Islands' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'CI', name: "Côte d'Ivoire" },
    { code: 'HR', name: 'Croatia' },
    { code: 'CU', name: 'Cuba' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DK', name: 'Denmark' },
    { code: 'DJ', name: 'Djibouti' },
    { code: 'DM', name: 'Dominica' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'EG', name: 'Egypt' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GQ', name: 'Equatorial Guinea' },
    { code: 'ER', name: 'Eritrea' },
    { code: 'EE', name: 'Estonia' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'FK', name: 'Falkland Islands' },
    { code: 'FO', name: 'Faroe Islands' },
    { code: 'FJ', name: 'Fiji' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'GF', name: 'French Guiana' },
    { code: 'PF', name: 'French Polynesia' },
    { code: 'TF', name: 'French Southern Territories' },
    { code: 'GA', name: 'Gabon' },
    { code: 'GM', name: 'Gambia' },
    { code: 'GE', name: 'Georgia' },
    { code: 'DE', name: 'Germany' },
    { code: 'GH', name: 'Ghana' },
    { code: 'GI', name: 'Gibraltar' },
    { code: 'GR', name: 'Greece' },
    { code: 'GL', name: 'Greenland' },
    { code: 'GD', name: 'Grenada' },
    { code: 'GP', name: 'Guadeloupe' },
    { code: 'GU', name: 'Guam' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'GG', name: 'Guernsey' },
    { code: 'GN', name: 'Guinea' },
    { code: 'GW', name: 'Guinea-Bissau' },
    { code: 'GY', name: 'Guyana' },
    { code: 'HT', name: 'Haiti' },
    { code: 'HM', name: 'Heard Island and McDonald Islands' },
    { code: 'VA', name: 'Holy See' },
    { code: 'HN', name: 'Honduras' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IR', name: 'Iran' },
    { code: 'IQ', name: 'Iraq' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IM', name: 'Isle of Man' },
    { code: 'IL', name: 'Israel' },
    { code: 'IT', name: 'Italy' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'JP', name: 'Japan' },
    { code: 'JE', name: 'Jersey' },
    { code: 'JO', name: 'Jordan' },
    { code: 'KZ', name: 'Kazakhstan' },
    { code: 'KE', name: 'Kenya' },
    { code: 'KI', name: 'Kiribati' },
    { code: 'KP', name: 'Korea, Democratic People\'s Republic of' },
    { code: 'KR', name: 'Korea, Republic of' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'KG', name: 'Kyrgyzstan' },
    { code: 'LA', name: 'Lao People\'s Democratic Republic' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LB', name: 'Lebanon' },
    { code: 'LS', name: 'Lesotho' },
    { code: 'LR', name: 'Liberia' },
    { code: 'LY', name: 'Libya' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MO', name: 'Macao' },
    { code: 'MK', name: 'Macedonia' },
    { code: 'MG', name: 'Madagascar' },
    { code: 'MW', name: 'Malawi' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'MV', name: 'Maldives' },
    { code: 'ML', name: 'Mali' },
    { code: 'MT', name: 'Malta' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'MQ', name: 'Martinique' },
    { code: 'MR', name: 'Mauritania' },
    { code: 'MU', name: 'Mauritius' },
    { code: 'YT', name: 'Mayotte' },
    { code: 'MX', name: 'Mexico' },
    { code: 'FM', name: 'Micronesia' },
    { code: 'MD', name: 'Moldova' },
    { code: 'MC', name: 'Monaco' },
    { code: 'MN', name: 'Mongolia' },
    { code: 'ME', name: 'Montenegro' },
    { code: 'MS', name: 'Montserrat' },
    { code: 'MA', name: 'Morocco' },
    { code: 'MZ', name: 'Mozambique' },
    { code: 'MM', name: 'Myanmar' },
    { code: 'NA', name: 'Namibia' },
    { code: 'NR', name: 'Nauru' },
    { code: 'NP', name: 'Nepal' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'NC', name: 'New Caledonia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'NE', name: 'Niger' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'NU', name: 'Niue' },
    { code: 'NF', name: 'Norfolk Island' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'NO', name: 'Norway' },
    { code: 'OM', name: 'Oman' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'PW', name: 'Palau' },
    { code: 'PS', name: 'Palestine' },
    { code: 'PA', name: 'Panama' },
    { code: 'PG', name: 'Papua New Guinea' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'PH', name: 'Philippines' },
    { code: 'PN', name: 'Pitcairn' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'QA', name: 'Qatar' },
    { code: 'RE', name: 'Réunion' },
    { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russian Federation' },
    { code: 'RW', name: 'Rwanda' },
    { code: 'BL', name: 'Saint Barthélemy' },
    { code: 'SH', name: 'Saint Helena' },
    { code: 'KN', name: 'Saint Kitts and Nevis' },
    { code: 'LC', name: 'Saint Lucia' },
    { code: 'MF', name: 'Saint Martin' },
    { code: 'PM', name: 'Saint Pierre and Miquelon' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines' },
    { code: 'WS', name: 'Samoa' },
    { code: 'SM', name: 'San Marino' },
    { code: 'ST', name: 'Sao Tome and Principe' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SN', name: 'Senegal' },
    { code: 'RS', name: 'Serbia' },
    { code: 'SC', name: 'Seychelles' },
    { code: 'SL', name: 'Sierra Leone' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SX', name: 'Sint Maarten' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'SB', name: 'Solomon Islands' },
    { code: 'SO', name: 'Somalia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'GS', name: 'South Georgia and the South Sandwich Islands' },
    { code: 'SS', name: 'South Sudan' },
    { code: 'ES', name: 'Spain' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'SD', name: 'Sudan' },
    { code: 'SR', name: 'Suriname' },
    { code: 'SJ', name: 'Svalbard and Jan Mayen' },
    { code: 'SZ', name: 'Swaziland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'SY', name: 'Syrian Arab Republic' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'TJ', name: 'Tajikistan' },
    { code: 'TZ', name: 'Tanzania' },
    { code: 'TH', name: 'Thailand' },
    { code: 'TL', name: 'Timor-Leste' },
    { code: 'TG', name: 'Togo' },
    { code: 'TK', name: 'Tokelau' },
    { code: 'TO', name: 'Tonga' },
    { code: 'TT', name: 'Trinidad and Tobago' },
    { code: 'TN', name: 'Tunisia' },
    { code: 'TR', name: 'Turkey' },
    { code: 'TM', name: 'Turkmenistan' },
    { code: 'TC', name: 'Turks and Caicos Islands' },
    { code: 'TV', name: 'Tuvalu' },
    { code: 'UG', name: 'Uganda' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'UM', name: 'United States Minor Outlying Islands' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'UZ', name: 'Uzbekistan' },
    { code: 'VU', name: 'Vanuatu' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'VN', name: 'Viet Nam' },
    { code: 'VG', name: 'Virgin Islands, British' },
    { code: 'VI', name: 'Virgin Islands, U.S.' },
    { code: 'WF', name: 'Wallis and Futuna' },
    { code: 'EH', name: 'Western Sahara' },
    { code: 'YE', name: 'Yemen' },
    { code: 'ZM', name: 'Zambia' },
    { code: 'ZW', name: 'Zimbabwe' },
];
</script>

<template>
    <div>
        <div v-if="state === 'success'" class="flex flex-col gap-3 items-center justify-center p-6 h-60">
            <h2 class="h4 font-semibold text-secondary-500">Thanks for your support!</h2>
            <p class="max-w-sm text-center leading-snug">Thanks for supporting {{ props.group.title }}, you should receive a confirmation email shortly.</p>
        </div>

        <div v-else class="flex flex-col gap-4 p-6">
            <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
                <div class="flex flex-col">
                    <label for="options[donationAmount]" class="form-label">Amount</label>

                    <div class="grid grid-cols-[3rem_1fr_3rem]">
                        <span class="relative z-20 flex items-center justify-center col-span-1 col-start-1 row-start-1 text-gray-500 [&+.form-input]:pl-10">
                            <svg class="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M184 24c0-13.3-10.7-24-24-24s-24 10.7-24 24V71.7c-3.1 .1-6.2 .3-9.3 .6c-23.2 1.9-47.2 7.4-67.2 20.1C38.7 105.6 23.5 126 18 154.2c-3.9 20.3-2 38.8 6.1 55.2c8 16 20.9 27.6 34.8 36.2c26.2 16.2 61.9 25.3 92.9 33.2l2.3 .6c33.9 8.6 62.6 16.1 81.7 28c9 5.6 14.3 11.2 17.1 16.9c2.7 5.3 4.2 12.8 2 24.5c-2.9 14.7-13.4 26.9-34.5 34.9c-21.6 8.2-52 10.9-87.6 5.9c-22.6-3.3-61.8-12.7-83-22.1c-12.1-5.4-26.3 .1-31.7 12.2s.1 26.3 12.2 31.7C57 423.2 101.1 433.4 126 437l.1 0c3.3 .5 6.6 .9 9.9 1.2V488c0 13.3 10.7 24 24 24s24-10.7 24-24V439.4c19.3-1.3 37.4-4.9 53.5-11c31.9-12.1 57.7-35.2 64.5-70.6c3.9-20.3 2-38.8-6.1-55.2c-8-16-20.9-27.6-34.8-36.2c-26.2-16.2-61.9-25.3-92.9-33.2l-2.3-.6c-33.9-8.6-62.6-16.1-81.7-28c-9-5.6-14.3-11.2-17.1-16.9c-2.7-5.3-4.2-12.8-2-24.5c2.9-14.8 10.1-24 20.2-30.4c10.9-6.9 26.3-11.2 45.3-12.7c38.3-3.1 83.1 5.3 113.9 12.5c12.9 3.1 25.8-4.9 28.9-17.8s-4.9-25.8-17.8-28.9c-18.7-4.4-44.2-9.7-71.5-12.5V24z" /></svg>
                        </span>

                        <input
                            id="options[donationAmount]"
                            v-model="amount"
                            type="number"
                            class="form-input relative z-10 col-span-3 col-start-1 row-start-1"
                            placeholder="Enter an amount"
                            step="0.01"
                            required
                        >
                    </div>
                </div>

                <dl class="grid grid-cols-2 gap-1 text-sm border border-gray-200 rounded-md p-3">
                    <dt>
                        Total sent to {{ props.group.title }}
                    </dt>
                    <dd class="text-right">
                        {{ amount ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount) : '—' }}
                    </dd>

                    <div class="col-span-2 border-t border-gray-100"></div>

                    <dt>
                        Platform fee
                    </dt>
                    <dd class="text-right">
                        {{ platformFee ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(platformFee) : '—' }}
                    </dd>
                </dl>

                <hr class="my-1">

                <div class="flex flex-col gap-3">
                    <div class="flex flex-col">
                        <label for="name" class="form-label">Name</label>

                        <div class="grid grid-cols-[3rem_1fr_3rem]">
                            <input
                                id="fullName"
                                type="text"
                                name="billingAddress[fullName]"
                                class="form-input relative z-10 col-span-3 col-start-1 row-start-1"
                                placeholder="e.g. John Doe"
                                required
                            >
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <label for="email" class="form-label">Email</label>

                        <div class="grid grid-cols-[3rem_1fr_3rem]">
                            <input
                                id="email"
                                type="email"
                                name="email"
                                class="form-input relative z-10 col-span-3 col-start-1 row-start-1"
                                placeholder="e.g. name@example.com"
                                required
                            >
                        </div>
                    </div>
                </div>

                <hr class="my-1">

                <fieldset class="flex flex-col gap-2">
                    <legend class="form-label">Billing info</legend>

                    <div class="flex flex-col">
                        <label for="billingAddress[countryCode]" class="sr-only">Country</label>

                        <div class="grid grid-cols-[3rem_1fr_3rem]">
                            <select id="billingAddress[countryCode]" name="billingAddress[countryCode]" class="form-input relative z-10 col-span-3 col-start-1 row-start-1">
                                <option v-for="country in countries" :key="country.code" :value="country.code" :selected="country.code === 'AU'">{{ country.name }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <label for="billingAddress[addressLine1]" class="sr-only">Address 1</label>

                        <div class="grid grid-cols-[3rem_1fr_3rem]">
                            <input
                                id="billingAddress[addressLine1]"
                                type="text"
                                name="billingAddress[addressLine1]"
                                class="form-input relative z-10 col-span-3 col-start-1 row-start-1"
                                placeholder="Address"
                                required
                            >
                        </div>
                    </div>

                    <div class="grid grid-cols-3 gap-2">
                        <div class="flex flex-col">
                            <label for="billingAddress[locality]" class="sr-only">City</label>

                            <div class="grid grid-cols-[3rem_1fr_3rem]">
                                <input
                                    id="billingAddress[locality]"
                                    type="text"
                                    name="billingAddress[locality]"
                                    class="form-input relative z-10 col-span-3 col-start-1 row-start-1"
                                    placeholder="City"
                                    required
                                >
                            </div>
                        </div>

                        <div class="flex flex-col">
                            <label for="billingAddress[administrativeArea]" class="sr-only">Sate</label>

                            <div class="grid grid-cols-[3rem_1fr_3rem]">
                                <input
                                    id="billingAddress[administrativeArea]"
                                    type="text"
                                    name="billingAddress[administrativeArea]"
                                    class="form-input relative z-10 col-span-3 col-start-1 row-start-1"
                                    placeholder="State"
                                    required
                                >
                            </div>
                        </div>

                        <div class="flex flex-col">
                            <label for="billingAddress[postalCode]" class="sr-only">Post code</label>

                            <div class="grid grid-cols-[3rem_1fr_3rem]">
                                <input
                                    id="billingAddress[postalCode]"
                                    type="text"
                                    name="billingAddress[postalCode]"
                                    class="form-input relative z-10 col-span-3 col-start-1 row-start-1"
                                    placeholder="Postcode"
                                    required
                                >
                            </div>
                        </div>
                    </div>
                </fieldset>

                <hr class="my-1">

                <input
                    type="hidden"
                    name="options[group]"
                    :value="props.group.title"
                >

                <input type="hidden" name="purchasableId" :value="donation.id">

                <div>
                    <p class="form-label">Payment details</p>
                    <div ref="card" class="form-input py-4"></div>
                </div>

                <div class="flex flex-col gap-1.5">
                    <ul v-if="errors.length">
                        <li v-for="error in errors" :key="error" class="text-sm text-red-500 px-4 py-2 bg-red-50 border border-red-100 rounded-md">{{ error }}</li>
                    </ul>

                    <button type="submit" class="btn btn-primary w-full mt-2" :class="{'loading': state === 'loading'}" :disabled="!amount">
                        Pay {{ amount ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(total) : null }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>
